import {
  faChevronDown,
  faChevronRight,
  faFile,
  faFileImport,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { useProjects } from "../state";
import { getCsrfToken } from "../state/csrf";

type ProjectFile = {
  id: number;
  name: string;
  category: string;
  content_type: string;
  size_bytes: number;
  modified: string;
  url: string;
};

const CATEGORIES = ["basisgegevens", "ontwikkeling", "omgeving", "bodem", "natuur"] as const;
const ALL_FOLDERS: readonly string[] = ["", ...CATEGORIES];
const ROOT_LABEL = "Project (root)";

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const FileRow = ({
  file,
  onDelete,
}: {
  file: ProjectFile;
  onDelete: (file: ProjectFile) => void;
}) => (
  <li className="group flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded">
    <FontAwesomeIcon icon={faFile} className="text-gray-400 w-3 h-3 shrink-0" />
    <a
      href={file.url}
      target="_blank"
      rel="noreferrer"
      className="text-xs text-blue-600 hover:underline truncate flex-1"
      title={file.name}
    >
      {file.name}
    </a>
    <span className="text-[11px] text-gray-400 shrink-0">{formatBytes(file.size_bytes)}</span>
    <button
      type="button"
      onClick={() => onDelete(file)}
      className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
      title={`Verwijder ${file.name}`}
      aria-label={`Verwijder ${file.name}`}
    >
      <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
    </button>
  </li>
);

const PRICE_PER_PARCEL_EUR = 2.96;

type EnrichSummary = {
  fetched: Array<{ parcel_id: number; filename: string }>;
  skipped: Array<{ parcel_id: number; reason: string }>;
  errors: Array<{ parcel_id: number; detail?: string; status?: number }>;
};

export const ProjectFiles = ({ projectId }: { projectId: number }) => {
  const [files, setFiles] = useState<ProjectFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const parcelCount = useProjects(
    (s) => s.projects?.find((p) => p.id === projectId)?.parcels?.length ?? 0
  );

  const fetchFiles = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(`/api/projects/${projectId}/files`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data: ProjectFile[] = await r.json();
      setFiles(data);
      return data;
    } catch {
      setError("Kon bestanden niet laden");
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    setFiles(null);
    setInitialized(false);
    setExpanded({});
    fetchFiles().then((data) => {
      if (!alive || !data) return;
      const firstWithFiles = ALL_FOLDERS.find((cat) => data.some((f) => f.category === cat));
      setExpanded(firstWithFiles ? { [firstWithFiles]: true } : {});
      setInitialized(true);
    });
    return () => {
      alive = false;
    };
  }, [projectId, fetchFiles]);

  const uploadFiles = useCallback(
    async (incoming: File[], category: string) => {
      if (!incoming.length) return;
      setUploading(true);
      setError(null);
      try {
        for (const f of incoming) {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("category", category);
          const r = await fetch(`/api/projects/${projectId}/files`, {
            method: "POST",
            headers: { "X-CSRFToken": getCsrfToken() },
            body: fd,
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
        }
        await fetchFiles();
        setExpanded((prev) => ({ ...prev, [category]: true }));
      } catch {
        setError("Upload mislukt");
      } finally {
        setUploading(false);
      }
    },
    [projectId, fetchFiles]
  );

  const handleDrop = (e: React.DragEvent, category: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    const dropped = Array.from(e.dataTransfer.files);
    void uploadFiles(dropped, category);
  };

  const handleDragOver = (e: React.DragEvent, category: string) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(category);
  };

  const handleKadasterEnrich = async () => {
    if (!parcelCount || enriching) return;
    const eur = (parcelCount * PRICE_PER_PARCEL_EUR).toFixed(2).replace(".", ",");
    const ok = window.confirm(
      `Eigendomsinformatie (Rechten) opvragen via Kadaster voor ${parcelCount} ` +
        `perceel${parcelCount === 1 ? "" : "en"}?\n\n` +
        `Maximale kosten: € ${eur} (€ 2,96 per perceel). ` +
        `Reeds opgehaalde percelen worden overgeslagen.`
    );
    if (!ok) return;
    setEnriching(true);
    setError(null);
    setInfo(null);
    try {
      const r = await fetch(`/api/projects/${projectId}/kadaster/enrich`, {
        method: "POST",
        headers: { "X-CSRFToken": getCsrfToken() },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const summary: EnrichSummary = await r.json();
      await fetchFiles();
      setExpanded((prev) => ({ ...prev, basisgegevens: true }));
      const parts: string[] = [];
      if (summary.fetched.length) parts.push(`${summary.fetched.length} opgehaald`);
      if (summary.skipped.length) parts.push(`${summary.skipped.length} overgeslagen`);
      if (summary.errors.length) parts.push(`${summary.errors.length} fout`);
      setInfo(parts.join(", ") || "Geen percelen gevonden");
    } catch {
      setError("Kadaster-aanvraag mislukt");
    } finally {
      setEnriching(false);
    }
  };

  const handleDelete = async (file: ProjectFile) => {
    if (!window.confirm(`Verwijder ${file.name}?`)) return;
    setError(null);
    try {
      const r = await fetch(`/api/projects/${projectId}/files/${file.id}`, {
        method: "DELETE",
        headers: { "X-CSRFToken": getCsrfToken() },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchFiles();
    } catch {
      setError("Verwijderen mislukt");
    }
  };

  if (!files && !error) return <p className="text-xs text-gray-400 p-2">Laden...</p>;

  const byCategory: Record<string, ProjectFile[]> = {};
  for (const cat of ALL_FOLDERS) byCategory[cat] = [];
  for (const f of files ?? []) (byCategory[f.category] ||= []).push(f);

  return (
    <div
      className="space-y-1 p-2"
      onDragOver={(e) => handleDragOver(e, "")}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragOver(null);
      }}
      onDrop={(e) => handleDrop(e, "")}
    >
      <button
        type="button"
        onClick={handleKadasterEnrich}
        disabled={!parcelCount || enriching}
        className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-xs font-semibold rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title={
          parcelCount
            ? `Maximaal € ${(parcelCount * PRICE_PER_PARCEL_EUR).toFixed(2).replace(".", ",")} (€ 2,96 per perceel)`
            : "Eerst percelen toevoegen"
        }
      >
        <FontAwesomeIcon icon={faFileImport} className="w-3 h-3 text-dark-green" />
        {enriching
          ? "Opvragen bij Kadaster..."
          : `Eigendomsinfo opvragen via Kadaster${parcelCount ? ` (${parcelCount}×)` : ""}`}
      </button>
      {info && <p className="text-xs text-green px-1 pt-1">{info}</p>}
      {error && <p className="text-xs text-red-500 px-1 pb-1">{error}</p>}
      {uploading && <p className="text-xs text-gray-500 px-1 pb-1">Uploaden...</p>}
      {initialized && (files?.length ?? 0) === 0 && !uploading && !error && (
        <p className="text-xs text-gray-400 px-1 pb-1">
          Sleep bestanden hierheen om ze te uploaden, of gebruik de WebDAV-koppeling.
        </p>
      )}
      {ALL_FOLDERS.map((cat) => {
        const rows = byCategory[cat];
        const isOpen = expanded[cat] ?? false;
        const isHover = dragOver === cat;
        return (
          <div
            key={cat || "root"}
            className={`rounded transition-colors ${
              isHover ? "bg-yellow-50 ring-1 ring-yellow-300" : ""
            }`}
            onDragOver={(e) => handleDragOver(e, cat)}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              setDragOver((prev) => (prev === cat ? null : prev));
            }}
            onDrop={(e) => handleDrop(e, cat)}
          >
            <button
              type="button"
              onClick={() => setExpanded((prev) => ({ ...prev, [cat]: !isOpen }))}
              className="w-full flex items-center gap-1 text-[11px] uppercase tracking-wide text-gray-500 px-1 py-0.5 hover:text-gray-700"
            >
              <FontAwesomeIcon
                icon={isOpen ? faChevronDown : faChevronRight}
                className="w-2.5 h-2.5"
              />
              <span>{cat || ROOT_LABEL}</span>
              <span className="text-gray-400 ml-1">({rows.length})</span>
            </button>
            {isOpen && rows.length > 0 && (
              <ul>
                {rows.map((file) => (
                  <FileRow key={file.id} file={file} onDelete={handleDelete} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};
